import { GetServerSideProps } from "next";
import { fetchAreaOverview } from "src/api/server/area-overview";
import { AreaOverviewModule } from "src/components/modules/base/area-overview-module";
import { ErrorBoundary } from "src/components-mui/common/error-boundary/error-boundary";
import { HomeSearchActionSchema } from "src/components-mui/common/schemas/home-search-action-schema";
import { isValidLocale } from "src/constants/locale";
import { AREA_BOTTOM_MARGIN, AREA_TOP_MARGIN, TOP_MARGIN_CONTENT_APP, TOP_MARGIN_NAV_LESS_PAGE } from "src/constants/styles";
import { HOME_SLUG } from "src/constants/urls";
import { useContentPageVisitTracking } from "src/hooks/use-content-page-visit-tracking";
import { useReturnUrl } from "src/hooks/use-return-url";
import { getAreaOverviewNamespaces } from "src/lib/i18n/area-overview-namespaces";
import { serverSideTranslations } from "src/lib/i18n/server-side-translations";
import { useIsCallerContentApp } from "src/lib/native-bridge/hooks/use-is-content-app";
import { ContentModuleType, IAreaOverviewProps } from "src/types/area-overview";
import { IBaseParam } from "src/types/common";
import { INav } from "src/types/layout";
import { addServerTiming } from "src/utils/nextjs/add-server-timing";
import { numberToPx } from "src/utils/number-format-utils";

type IPageProps = IAreaOverviewProps & { isHomePage: boolean };

const isHomePageSlug = (slug: string[]) => slug.join("/") === HOME_SLUG;

/** The general spacing step between content-modules */
const MODULE_SPACING_STEP = 80;
/** The spacing step of single spacing-modules */
const SPACING_MODULE_STEP = 40;

const getSpacing = (spacingStep: number, factorPx: number) => {
    const spacingDesktop = spacingStep * factorPx;
    const spacingSmall = spacingDesktop / 2;

    return [numberToPx(spacingSmall), numberToPx(spacingSmall), numberToPx(spacingDesktop)];
};

interface IGetFirstModuleTopMarginParams {
    navigation: INav | null;
    isCallerContentApp: boolean;
}

const getFirstModuleTopMargin = ({ navigation, isCallerContentApp }: Readonly<IGetFirstModuleTopMarginParams>) => {
    if (isCallerContentApp) {
        return TOP_MARGIN_CONTENT_APP;
    }

    if (navigation === null) {
        return TOP_MARGIN_NAV_LESS_PAGE;
    }

    return AREA_TOP_MARGIN;
};

const PageTracking = ({ returnUrl }: { returnUrl?: string }) => {
    useReturnUrl(returnUrl);
    useContentPageVisitTracking();
    return null;
};

function Page({ data, navigation, isHomePage }: Readonly<IPageProps>) {
    const isCallerContentApp = useIsCallerContentApp();
    const { spacingStep: _spacingStep, modules } = data;
    const firstModuleTopMargin = getFirstModuleTopMargin({ navigation, isCallerContentApp });

    const spacing = getSpacing(_spacingStep, MODULE_SPACING_STEP);
    const getMarginBottom = (currentIndex: number) => {
        if (currentIndex === modules.length - 1) {
            // Last content module
            return AREA_BOTTOM_MARGIN;
        }

        const nextModule = modules[currentIndex + 1];
        if (nextModule.type === ContentModuleType.SPACING) {
            // Apply custom spacing if next is spacing module
            return getSpacing(nextModule.spacingStep, SPACING_MODULE_STEP);
        }

        return spacing;
    };

    return (
        <>
            <PageTracking returnUrl={data.returnUrl} />
            {isHomePage && <HomeSearchActionSchema />}
            {modules
                .map((module, index) => {
                    const mt = index === 0 ? firstModuleTopMargin : undefined;
                    const mb = getMarginBottom(index);

                    return (
                        <ErrorBoundary key={`${module.id}-${index}`}>
                            <AreaOverviewModule moduleProps={{ ...module, isFirstModule: index === 0 }} sectionProps={{ mt, mb }} />
                        </ErrorBoundary>
                    );
                })
                .filter(Boolean)}
        </>
    );
}

type IParam = IBaseParam & {
    slug: string[];
};

export const getServerSideProps: GetServerSideProps<IPageProps, IParam> = async context => {
    const { language, slug } = context.params!;
    const { country } = context.params!;

    if (!isValidLocale({ language, country })) {
        return { notFound: true };
    }

    const [props, translations] = await addServerTiming(context.res, "ssprops", async () => {
        const props = await fetchAreaOverview(slug.join("/"), context);

        const translations = await serverSideTranslations(language, getAreaOverviewNamespaces(props?.data?.modules));

        return [props, translations];
    });

    const redirectUrl = props?.data?.redirectUrl;
    if (redirectUrl) {
        return {
            redirect: {
                destination: redirectUrl,
                permanent: false,
            },
        };
    }

    if (!props) {
        return { notFound: true };
    }

    return { props: { ...props, ...translations, isHomePage: isHomePageSlug(slug) } };
};

export default Page;
